import { Pagination } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { ReactNode } from 'react';
import { Grid, GridCell, GridHeader, GridLabels, GridRow } from '../Grid/Grid';
import OrderBy from '../OrderBy/OrderBy';
import { PaginationNav } from '../PaginationNav/PaginationNav';
import './TableGrid.scss';

type Entity = {
  id: number | string;
  rowClassName?: string;
  [key: string]: any;
};

export type DataItem<T = { [key: string]: any }> = {
  label: string | ReactNode;
  onChange?: (...params: any[]) => any;
  keyName?: keyof T;
  renderCell?: (item: Entity, key?: string | number | symbol) => JSX.Element;
  fr?: number;
  className?: string;
};

export type Props = {
  onRowClick?: (entityId: number | string, entity: Entity) => any;
  data: DataItem<any>[];
  items: Entity[];
  className?: string;
  labelsClassName?: string;
  headerComponent?: any;
  hideEmptyHeader?: boolean;
  hideLabels?: boolean;
  pagination?: Pagination;
  showRangedPagination?: boolean;
  paginationRangeLength?: number;
  onChangePagination?: (pagingNumber: number) => void;
  orderByAtTheEnd?: boolean;
  gridTemplateColumns?: string;
};

export const defaultRenderCell = (keyName: string, item: Entity) => (
  <GridCell key={keyName}>{item && item[keyName]}</GridCell>
);

export const TableLabel = ({
  dataItem,
  orderByAtTheEnd,
}: {
  dataItem: DataItem;
  orderByAtTheEnd?: boolean;
}) => {
  const { label, keyName, onChange, className, fr = 1 } = dataItem;
  return (
    <GridCell key={keyName} className={className} fr={fr}>
      {orderByAtTheEnd && label}
      {onChange && (
        <OrderBy
          alignRight={orderByAtTheEnd}
          onChange={onChange}
          keyName={keyName as string}
        />
      )}
      {!orderByAtTheEnd && label}
    </GridCell>
  );
};

const TableGrid = (props: Props) => {
  const {
    paginationRangeLength,
    headerComponent,
    pagination,
    showRangedPagination,
    onChangePagination,
    items,
    className,
    labelsClassName,
    onRowClick,
    data,
    orderByAtTheEnd,
    gridTemplateColumns,
    hideEmptyHeader,
    hideLabels,
  } = props;

  return (
    <>
      <Grid className={className}>
        {(headerComponent || !hideEmptyHeader) && (
          <GridHeader>{headerComponent}</GridHeader>
        )}

        {!hideLabels && (
          <GridLabels
            gridTemplateColumns={gridTemplateColumns}
            className={labelsClassName}
          >
            {data.map(dataItem => (
              <TableLabel
                key={(dataItem.keyName as string) || dataItem.label?.toString()}
                dataItem={dataItem}
                orderByAtTheEnd={orderByAtTheEnd}
              />
            ))}
          </GridLabels>
        )}

        {items.map(item => (
          <GridRow
            className={classnames(item.rowClassName, 'table-grid__table-row')}
            key={item.id}
            onClick={() => (onRowClick ? onRowClick(item.id, item) : null)}
            gridTemplateColumns={gridTemplateColumns}
          >
            {data.map(({ renderCell, keyName, fr }, i) => {
              return renderCell ? (
                <span key={`${String(keyName)}_${String(i)}`}>
                  {renderCell(item, keyName)}
                </span>
              ) : (
                defaultRenderCell(keyName as string, item)
              );
            })}
          </GridRow>
        ))}
      </Grid>

      {!!items.length && !!pagination && !!onChangePagination && (
        <PaginationNav
          className="table-grid__pagination"
          pagination={pagination}
          withEllipsis={Boolean(showRangedPagination)}
          onChangePage={onChangePagination}
          rangeLength={paginationRangeLength}
        />
      )}
    </>
  );
};

export default TableGrid;
